import './styles.css';
import { Group } from 'three';
import { ViewerCore } from './core/ViewerCore';
import { ModelManager } from './models/ModelManager';
import { AppShell } from './ui/AppShell';
import { ThemeController } from './theme/ThemeController';

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('Application root was not found.');

const modelsRoot = new Group();
const modelManager = new ModelManager(modelsRoot);
const themeController = new ThemeController();
const shell = new AppShell(root, modelManager, (files) => {
  void modelManager.addFiles(files);
}, themeController);
const viewer = new ViewerCore(shell.elements.viewport, modelsRoot);
viewer.setTheme(themeController.current);
themeController.changed.subscribe((theme) => viewer.setTheme(theme));

viewer.selection.changed.subscribe((selection) => {
  if (!selection) {
    shell.hideSelectionMenu();
    return;
  }
  shell.showSelectionMenu(
    selection.clientX,
    selection.clientY,
    () => viewer.selection.focusSelected(),
    () => viewer.selection.clear(),
    () => {
      viewer.boxCut.clear();
      viewer.section.start(selection.point, selection.normal);
      viewer.selection.clear();
    },
    () => {
      viewer.section.clear();
      viewer.boxCut.start(selection.mesh);
      viewer.selection.clear();
    },
  );
});

shell.bindSectionControls(
  (offset) => viewer.section.setOffset(offset),
  () => viewer.section.flip(),
  () => viewer.section.clear(),
);
viewer.section.changed.subscribe((section) => {
  shell.updateSectionGizmo(section.active ? viewer.getSectionGizmoPosition() : null, section.offset);
});
shell.elements.viewport.addEventListener('sectiondragstate', (event) => {
  viewer.setNavigationEnabled(!(event as CustomEvent<boolean>).detail);
});
const updateSectionGizmo = (): void => {
  shell.updateSectionGizmo(viewer.getSectionGizmoPosition(), viewer.section.currentOffset);
  requestAnimationFrame(updateSectionGizmo);
};
updateSectionGizmo();
shell.bindBoxCutControls((face, delta) => viewer.boxCut.moveFace(face, delta), () => viewer.boxCut.clear());
const updateBoxGizmo = (): void => {
  shell.updateBoxGizmo(viewer.getBoxGizmoPositions());
  requestAnimationFrame(updateBoxGizmo);
};
updateBoxGizmo();

modelManager.changed.subscribe((state) => {
  viewer.selection.synchronize();
  viewer.section.synchronize();
  viewer.boxCut.synchronize();
  if (state.models.some((model) => model.status === 'ready')) viewer.fitAll();
});
shell.elements.fitButton.addEventListener('click', () => viewer.fitAll());

window.addEventListener('beforeunload', () => viewer.dispose(), { once: true });

